import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { Check, ChevronDown } from "lucide-react";
import Select, {
  components,
  ControlProps,
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  GroupBase,
  OptionProps,
  SingleValue,
  StylesConfig,
} from "react-select";

type SelectSize = "sm" | "md";
type SelectVariant = "default" | "secondary" | "active";

interface SelectOption {
  value: string | null;
  label: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value?: SelectOption | null;
  onChange: (newValue: SingleValue<SelectOption>) => void;
  menuPosition?: "fixed" | "absolute";
  menuPlacement?: "auto" | "top" | "bottom";

  size?: SelectSize;
  variant?: SelectVariant;
  className?: string;
  placeholder?: string;
  menuClassName?: CSSObjectWithLabel;
}

const triggerClasses = cva(
  "flex gap-4 capitalize items-center justify-between whitespace-nowrap rounded-lg focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border border-input bg-transparent hover:bg-accent",
        active:
          "border border-primary/80 text-primary bg-background hover:border-primary/60 hover:bg-accent",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
      },
      size: {
        md: "py-3 px-5 !leading-5 max-h-11 text-base",
        sm: "py-[10px] px-4 text-xs sm:text-sm leading-[17.5px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

const CustomSelect = ({
  options,
  value,
  onChange,
  size = "md",
  variant = "default",
  className,
  placeholder,
  menuClassName,
  menuPlacement = "auto",
  menuPosition = "absolute",
}: CustomSelectProps) => {
  const customStyles: StylesConfig<
    SelectOption,
    false,
    GroupBase<SelectOption>
  > = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      padding: 0,
      cursor: "pointer",
      "&:hover": {
        border: "none",
      },
    }),
    menu: (provided) => ({
      ...provided,
      border: "1px solid var(--border)",
      borderRadius: "0.5rem",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      marginTop: "4px",
      zIndex: 50,
      ...menuClassName,
    }),
    option: (provided) => ({
      ...provided,
      backgroundColor: "transparent",
      color: "inherit",
      cursor: "pointer",
      padding: size === "sm" ? "6px 8px" : "8px 12px",
      fontSize: size === "sm" ? "14px" : "16px",
      "&:hover": {
        backgroundColor: "hsl(var(--accent))",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "inherit",
      marginLeft: 0,
      fontSize: size === "sm" ? "14px" : "16px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: 0,
      color: "text-gray-800",
    }),
    placeholder: (base) => ({
      ...base,
      color: "text-gray-800",
    }),
  };

  const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption>) => {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronDown className={size === "sm" ? "size-4" : "size-4"} />
      </components.DropdownIndicator>
    );
  };

  const Option = (props: OptionProps<SelectOption>) => {
    return (
      <components.Option {...props}>
        <div className="flex items-center justify-between w-full capitalize">
          {props.label}
          {props.isSelected && (
            <Check className={size === "sm" ? "size-3" : "size-4"} />
          )}
        </div>
      </components.Option>
    );
  };

  const Control = ({ children, ...props }: ControlProps<SelectOption>) => {
    return (
      <components.Control {...props}>
        <div className={cn(triggerClasses({ variant, size, className }))}>
          {children}
        </div>
      </components.Control>
    );
  };

  return (
    <Select
      options={options}
      value={value}
      onChange={onChange}
      styles={customStyles}
      components={{
        DropdownIndicator,
        Option,
        Control,
      }}
      placeholder={placeholder}
      isSearchable={false}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
    />
  );
};

export default CustomSelect;
